/**
 * KAN-929: лента переписки = сообщения + звонки менеджера по времени.
 *
 * Порядок сообщений задаёт backend (Coalesce(spoken_at, created_at): реплики
 * звонка бота пишутся позже, чем сказаны, KAN-321), здесь он НЕ меняется.
 * Звонки (по opened_at) вставляются в готовый список: звонок встаёт перед
 * первым сообщением, чьё время отображения (spoken_at, иначе created_at)
 * позже opened_at; при равном времени сообщение раньше звонка.
 * hasMore (выше есть незагруженные ранние сообщения): звонки раньше первого
 * загруженного сообщения скрыты, чтобы не всплывать над ещё не подгруженной
 * перепиской. Без сообщений звонки идут все.
 */
import type { ClientCall } from '@/types/clientCall';
import type { EstimateMessage } from '@/types/estimate';

export type ConversationFeedItem =
  | { kind: 'message'; id: string; at: string; message: EstimateMessage }
  | { kind: 'call'; id: string; at: string; call: ClientCall };

interface MergeOptions {
  hasMore: boolean;
}

function time(value: string): number {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Время, по которому сообщение стоит в ленте (как сортирует backend). */
export function messageFeedTime(message: EstimateMessage): string {
  return message.spoken_at || message.created_at;
}

export function mergeFeedWithCalls(
  messages: readonly EstimateMessage[],
  calls: readonly ClientCall[],
  { hasMore }: MergeOptions
): ConversationFeedItem[] {
  const messageItems: ConversationFeedItem[] = messages.map((message) => ({
    kind: 'message',
    id: message.id,
    at: messageFeedTime(message),
    message,
  }));
  if (calls.length === 0) return messageItems;

  const firstAt = messageItems.length > 0 ? time(messageItems[0].at) : null;
  const sortedCalls = [...calls]
    .filter((call) => !(hasMore && firstAt !== null && time(call.opened_at) < firstAt))
    .sort((a, b) => time(a.opened_at) - time(b.opened_at));

  const result: ConversationFeedItem[] = [];
  let callIndex = 0;
  for (const item of messageItems) {
    const itemAt = time(item.at);
    while (callIndex < sortedCalls.length && time(sortedCalls[callIndex].opened_at) < itemAt) {
      const call = sortedCalls[callIndex];
      result.push({ kind: 'call', id: call.id, at: call.opened_at, call });
      callIndex += 1;
    }
    result.push(item);
  }
  for (const call of sortedCalls.slice(callIndex)) {
    result.push({ kind: 'call', id: call.id, at: call.opened_at, call });
  }
  return result;
}
